import { API } from '../config/axiosConfig';
import endpoints from '../endpoints/endpoints';

const sellarModule = {
  getSellarProfiles: async (params = {}) => {
    try {
      const { index = 0, offset = 10, ...otherParams } = params;
      const queryParams = new URLSearchParams({
        index: String(index),
        offset: String(offset),
        ...Object.fromEntries(
          Object.entries(otherParams).filter(([_, v]) => v !== undefined && v !== null && v !== '')
        ),
      });

      const response = await API.get(`api/users/seller-profiles/?${queryParams.toString()}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch profiles',
      };
    }
  },

  updateSellerStatus: async (id, status, reason) => {
    try {
      const payload = reason ? { status, remarks: reason } : { status };
      const response = await API.patch(`api/users/seller-profiles/${id}/update-status/`, payload);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update seller status',
      };
    }
  },

  updateSellerAutoApproval: async (id, autoApprove) => {
    try {
      const payload = { auto_approve: autoApprove };
      const response = await API.patch(`api/users/seller-profiles/${id}/update-status/`, payload);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update auto-approval setting',
      };
    }
  },

  getCardListings: async (params = {}) => {
    try {
      // `limit` is correct HERE and only here: `get-card-listings` reads
      // `page` + `limit` (apps/api/apps/dashboard/views.py:85-86) while the
      // two order-list endpoints in the same app read `page_size`. Do not
      // "make this consistent" with getSellerOrders — the inconsistency is
      // the backend's, and translating it away here would break this call.
      const response = await API.get('api/dashboard/get-card-listings', {
        params,
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch card listings',
      };
    }
  },

  getSellerRecentReviews: async (params = { index: 0, offset: 10 }) => {
    try {
      const response = await API.get('api/dashboard/seller-recent-reviews', {
        params,
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch seller reviews',
      };
    }
  },

  getSellerProfitLoss: async (filters = {}) => {
    try {
      const params = {};

      if (filters.year) params.year = filters.year;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.type) params.type = filters.type;

      const response = await API.get('api/dashboard/seller-profit-loss', {
        params: Object.keys(params).length ? params : undefined,
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch seller profit/loss',
      };
    }
  },

  getSellerDashboardStats: async (params = { index: 0, offset: 10 }) => {
    try {
      const response = await API.get('api/dashboard/seller-dashboard-stats', {
        params,
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch seller dashboard stats',
      };
    }
  },

  // ✅ New API for seller transaction history
  getSellerTransactionHistory: async (params = { page: 1, limit: 5 }) => {
    try {
      const response = await API.get('api/dashboard/seller-transaction-history', { params });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch seller transaction history',
      };
    }
  },

  getSellerOrders: async (params = { page: 1, limit: 5 }) => {
    try {
      // `seller-order-list` reads `page` + `page_size`
      // (apps/api/apps/dashboard/views.py:458), NOT `limit` — unlike
      // `getCardListings` below, which hits `get-card-listings` in the same
      // Django app and really does read `limit`. Sending `limit` here is
      // silently ignored and the view falls back to its own default of 10.
      // An explicit `page_size` wins; `limit` is the legacy spelling.
      const { limit, ...rest } = params;
      const response = await API.get('api/dashboard/seller-order-list', {
        params:
          limit === undefined || rest.page_size !== undefined
            ? rest
            : { ...rest, page_size: limit },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch seller orders',
      };
    }
  },

  getAllOrders: async (params = { page: 1, limit: 5 }) => {
    try {
      // `get-all-orders` reads `page_size` too
      // (apps/api/apps/dashboard/views.py:571). Same translation as
      // getSellerOrders above — see the comment there for why.
      const { limit, ...rest } = params;
      const response = await API.get('api/dashboard/get-all-orders', {
        params:
          limit === undefined || rest.page_size !== undefined
            ? rest
            : { ...rest, page_size: limit },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch seller orders',
      };
    }
  },

  // Update card listing
  updateCardListing: async (formData) => {
    try {
      const response = await API.put(endpoints.updateCardListing, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        success: true,
        data: response.data,
        message: response.data.message || 'Card listing updated successfully',
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to update card listing',
      };
    }
  },

  // Delete card listing
  deleteCardListing: async (listId) => {
    try {
      const response = await API.delete(`api/cards/delete-card-listing/?list_id=${listId}`);

      return {
        success: true,
        data: response.data,
        message: response.data.message || 'Card listing deleted successfully',
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to delete card listing',
      };
    }
  },

  // Bulk delete card listings
  bulkDeleteCardListings: async (listingIds) => {
    try {
      const response = await API.delete('api/cards/bulk-delete-card-listings/', {
        data: { listing_ids: listingIds },
      });

      return {
        success: true,
        data: response.data,
        message: response.data.message || 'Listings deleted successfully',
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to delete listings',
      };
    }
  },

  updateStoreAvailability: async (isUnavailable) => {
    try {
      const response = await API.post('/api/users/store/availability/', {
        is_unavailable: isUnavailable,
      });
      return {
        success: true,
        data: response.data,
        message: response.data?.message || 'Store availability updated',
      };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message || error.message || 'Failed to update store availability',
      };
    }
  },

  updateStoreAvailabilityAdmin: async (sellerId, isUnavailable) => {
    try {
      const response = await API.post('/api/users/admin/store/availability/', {
        seller_id: sellerId,
        is_unavailable: isUnavailable,
      });
      return {
        success: true,
        data: response.data,
        message: response.data?.message || 'Store availability updated',
      };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message || error.message || 'Failed to update store availability',
      };
    }
  },
};

export default sellarModule;
